/**
 * 🔐 Auth verify resolver — distinguish "token rejected" from "backend can't tell us"
 *
 * Shared between native cold-launch (`web/src/app/index.tsx`) and web
 * session-restore (`web/src/contexts/AuthContext.tsx`). One implementation,
 * one place to update.
 *
 * WHY THIS EXISTS
 * ───────────────
 * On 2026-05-20 the ALB returned hundreds of 5xx responses at the load
 * balancer layer (HTTPCode_ELB_5XX_Count peaked at 178 in one 10-min
 * bucket; target-layer 5xx was 1). The pre-fix client code at two call
 * sites — index.tsx:78-91 and AuthContext.tsx:216-244 — treated ANY
 * non-2xx response from GET /api/auth/me as "token invalid" and
 * cleared the user's stored tokens, forcing them through full Auth0
 * re-authentication.
 *
 * This was the SAME regression class as the welcome-screen bug fixed
 * in PR #1420/#1421 — mistaking "backend can't tell us right now" for
 * "user is invalid." The symptom differs (forced re-login vs. forced
 * re-onboarding) but the cause is identical.
 *
 * THE CONTRACT
 * ────────────
 * 1. ONE retry on 5xx with jittered 500-1000ms backoff. Without jitter,
 *    simultaneous cold-launches during an ALB blip synchronize their
 *    retries and amplify the storm. 4xx does NOT retry — 401/403 are
 *    unambiguous "token rejected" signals; retrying with the same dead
 *    token is wasted load.
 *
 * 2. Three discriminated result kinds:
 *      - verified         — 200, response body returned to caller
 *      - unauthorized     — 401 or 403; caller MAY clear tokens
 *      - transient_failure — 5xx after retry OR network error / parse fail;
 *                            caller MUST keep tokens (offline-tolerant)
 *
 * 3. Resolver does NOT touch token storage itself. Token-clearing is
 *    the caller's decision based on the result kind. If the resolver
 *    cleared tokens, the caller couldn't preserve them on
 *    transient_failure — defeating the whole fix.
 *
 * UnifiedAuth0Provider.tsx:148-198 already implements the right pattern
 * inline (401/403 → refresh → clear if refresh fails; other non-2xx →
 * keep tokens). It is NOT migrated to this resolver in this PR — the
 * refresh-token flow is Auth0-specific and shouldn't be hidden inside a
 * generic verify helper. Future refactor candidate.
 *
 * Locked in by web/src/__tests__/authVerifyResolver.test.ts.
 */

// ───────────────────────────────────────────────────────────────────────
// Tunable constants
// ───────────────────────────────────────────────────────────────────────

/** Jittered retry-delay floor on 5xx (ms). */
export const RETRY_DELAY_MIN_MS = 500;

/** Jittered retry-delay ceiling on 5xx (ms). */
export const RETRY_DELAY_MAX_MS = 1000;

// ───────────────────────────────────────────────────────────────────────
// Types
// ───────────────────────────────────────────────────────────────────────

/**
 * Discriminated result of a verifyAuth attempt.
 *
 *   - `verified`         — Token accepted. `data` is the response body
 *                          (typically {user_id, email, is_admin,
 *                          feature_flags, needs_email_verification}).
 *   - `unauthorized`     — 401 or 403. `status` carries the exact code
 *                          so the caller can distinguish if needed.
 *                          Caller MAY clear tokens / re-auth.
 *   - `transient_failure` — Could not determine. 5xx after retry,
 *                          network error, parse error. Caller MUST
 *                          preserve tokens and proceed offline-tolerantly.
 *                          The next user-driven request will surface a
 *                          real failure if the token genuinely is dead.
 */
export type VerifyAuthResult =
  | { kind: 'verified'; data: AuthMeResponse }
  | { kind: 'unauthorized'; status: 401 | 403 }
  | { kind: 'transient_failure'; reason: 'network' | 'parse' | 'http_5xx' | 'unexpected_4xx'; status?: number };

/**
 * The shape /api/auth/me returns. Mirrors routers/auth.py:262-268.
 * `needs_email_verification` was added 2026-05 to drive the cold-launch
 * email-verification wall (web/src/app/index.tsx:108).
 */
export interface AuthMeResponse {
  user_id: string;
  email?: string | null;
  is_admin?: boolean;
  feature_flags?: Record<string, unknown>;
  needs_email_verification?: boolean;
  /**
   * Per-user health data consent timestamp (ISO string) — when the user
   * first granted Coach Cedar permission to ingest HealthKit / Health
   * Connect data. `null` means no consent on file. Added 2026-05-29 by
   * the per-user-health-consent sprint (Phase 1.6); the iOS / Android
   * client mirrors this via `services/healthConsentSync.ts` to gate
   * sync calls without relying on OS-level device permission (which is
   * per-app per-device, not per-user, and inherits on shared devices).
   */
  health_data_consent_at?: string | null;
  /**
   * ISO timestamp of the user's earliest plan (MIN(user_plans.created_at)), or
   * `null` if they have no plan yet. Added 2026-06 by the notifications-first
   * onboarding Phase 2; the native client mirrors it via
   * `services/firstPlanSync.ts` as the durable anchor for the ~48h-post-first-
   * plan notification re-ask.
   */
  first_plan_created_at?: string | null;
  /**
   * D5 (2026-09-22): the triathlon coaching tier — `'gated'` outside the
   * coaching tiers, `'included'` inside, absent when the server's gate is off.
   * Folded into the flags cache (featureFlagsSync `tri_coaching_tier`).
   */
  tri_coaching_tier?: 'included' | 'gated';
  /**
   * Some legacy callers expect a nested {user: {...}} shape. The flat
   * fields above are canonical; this is here only so TypeScript doesn't
   * complain when call sites read `data.user?.sub`.
   */
  user?: { sub: string; email?: string };
}

export interface VerifyAuthOptions {
  /** Abort signal — AbortError propagates so caller timeouts work. */
  signal?: AbortSignal;
}

// ───────────────────────────────────────────────────────────────────────
// Internal helpers
// ───────────────────────────────────────────────────────────────────────

function jitteredDelay(): number {
  return RETRY_DELAY_MIN_MS + Math.random() * (RETRY_DELAY_MAX_MS - RETRY_DELAY_MIN_MS);
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      const e = new Error('AbortError');
      e.name = 'AbortError';
      reject(e);
      return;
    }
    const timer = setTimeout(resolve, ms);
    if (signal) {
      signal.addEventListener(
        'abort',
        () => {
          clearTimeout(timer);
          const e = new Error('AbortError');
          e.name = 'AbortError';
          reject(e);
        },
        { once: true },
      );
    }
  });
}

async function fetchOnce(
  apiBaseUrl: string,
  jwt: string,
  signal?: AbortSignal,
): Promise<Response> {
  return fetch(`${apiBaseUrl}/api/auth/me`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${jwt}`,
      'Content-Type': 'application/json',
    },
    signal,
  });
}

// ───────────────────────────────────────────────────────────────────────
// Public API
// ───────────────────────────────────────────────────────────────────────

/**
 * Verify a JWT against `/api/auth/me` with ALB-instability resilience.
 *
 * Flow:
 *   1. GET /api/auth/me
 *   2. On status >= 500, wait jittered 500-1000ms and try once more.
 *   3. On 200 → { kind: 'verified', data }
 *   4. On 401 or 403 → { kind: 'unauthorized', status }
 *   5. On 5xx after retry → { kind: 'transient_failure', reason: 'http_5xx' }
 *   6. On other non-2xx (rare — 404, 422, etc.) → { kind: 'transient_failure' }
 *      (we don't have a meaningful interpretation; safest is to preserve
 *      tokens and let the caller proceed offline-tolerantly).
 *   7. On network error (fetch throws) → { kind: 'transient_failure', reason: 'network' }
 *   8. On JSON parse error (200 but unparseable body) →
 *      { kind: 'transient_failure', reason: 'parse' }
 *
 * AbortError always propagates so caller timeouts work.
 */
export async function verifyAuth(
  apiBaseUrl: string,
  jwt: string,
  options?: VerifyAuthOptions,
): Promise<VerifyAuthResult> {
  const signal = options?.signal;

  let response: Response | null = null;
  try {
    response = await fetchOnce(apiBaseUrl, jwt, signal);
    if (response.status >= 500) {
      // Jittered backoff — Math.random() spreads cold-launches across
      // the 500-1000ms window so simultaneous retries don't pile up
      // during an ALB blip.
      await sleep(jitteredDelay(), signal);
      response = await fetchOnce(apiBaseUrl, jwt, signal);
    }
  } catch (err) {
    if ((err as Error)?.name === 'AbortError') throw err;
    return { kind: 'transient_failure', reason: 'network' };
  }

  if (response.ok) {
    try {
      const data = (await response.json()) as AuthMeResponse;
      return { kind: 'verified', data };
    } catch (_err) {
      // 200 OK but body unparseable. Treat as transient — caller
      // preserves tokens and the next request will refetch.
      return { kind: 'transient_failure', reason: 'parse' };
    }
  }

  if (response.status === 401 || response.status === 403) {
    return { kind: 'unauthorized', status: response.status as 401 | 403 };
  }

  if (response.status >= 500) {
    // Already retried once; second attempt also failed.
    return { kind: 'transient_failure', reason: 'http_5xx', status: response.status };
  }

  // Other 4xx (404, 422, etc.). The endpoint shouldn't normally return
  // these — but if it does, we don't have a confident interpretation.
  // Preserve tokens and let the next user-driven request surface a real
  // problem if there is one.
  return { kind: 'transient_failure', reason: 'unexpected_4xx', status: response.status };
}
